import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import {
  Shield,
  Calendar,
  Clock,
  MoreVertical,
  FileText,
  CalendarPlus,
  History,
  XCircle,
  CalendarClock,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format, parseISO, differenceInDays } from 'date-fns';
import { getComplianceItemsForAsset } from '@/services/compliance-service';

// Import compliance action panels
import { RecordInspectionPanel } from '@/components/compliance/record-inspection-panel';
import { ScheduleInspectionPanel } from '@/components/compliance/schedule-inspection-panel';
import { ComplianceHistoryPanel } from '@/components/compliance/compliance-history-panel';
import { MarkNonApplicablePanel } from '@/components/compliance/mark-non-applicable-panel';
import { DeferDueDatePanel } from '@/components/compliance/defer-due-date-panel';

interface ComplianceItem {
  id: string;
  assetId: string;
  assetName: string;
  regulation: string;
  description?: string;
  frequency: string;
  status: string;
  nextInspectionDate?: string;
  lastInspectionDate?: string;
  lastInspectionResult?: string;
  assignedInspector?: string;
  certificationNumber?: string;
  priority?: string;
}

interface AssetDetailRegulatoryComplianceProps {
  readonly assetId: string;
  readonly assetName: string;
  readonly assetStatus?: string;
}

type ActionType = 'record' | 'schedule' | 'history' | 'mark-na' | 'defer' | null;

export function AssetDetailRegulatoryCompliance({
  assetId,
  assetName,
  assetStatus,
}: AssetDetailRegulatoryComplianceProps) {
  const [complianceItems, setComplianceItems] = useState<ComplianceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<ComplianceItem | null>(null);
  const [activeAction, setActiveAction] = useState<ActionType>(null);
  const [showHistory, setShowHistory] = useState(false);

  const isAssetDisposed = assetStatus === 'Disposed' || assetStatus === 'Sold';

  useEffect(() => {
    loadComplianceItems();
  }, [assetId]);

  const loadComplianceItems = async () => {
    setLoading(true);
    try {
      const items = await getComplianceItemsForAsset(assetId);
      setComplianceItems(items);
    } catch (error) {
      console.error('Failed to load compliance items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleActionClick = (item: ComplianceItem, action: ActionType) => {
    setSelectedItem(item);
    setActiveAction(action);
  };

  const handleActionClose = () => {
    setActiveAction(null);
    setSelectedItem(null);
    loadComplianceItems(); // Refresh data after action
  };

  const handlePanelSubmit = async (data: any) => {
    // Handle the submission - this would call the appropriate service method
    console.log('Panel submitted:', data);
    handleActionClose();
  };

  const getComplianceStatus = (item: ComplianceItem) => {
    if (item.status === 'Non-Applicable') {
      return {
        label: 'Non-Applicable',
        variant: 'secondary' as const,
        color: 'text-muted-foreground',
        bgColor: 'bg-muted',
      };
    }

    if (!item.nextInspectionDate) {
      return {
        label: 'Not Scheduled',
        variant: 'outline' as const,
        color: 'text-muted-foreground',
        bgColor: 'bg-muted/50',
      };
    }

    const daysUntilDue = differenceInDays(parseISO(item.nextInspectionDate), new Date());

    if (daysUntilDue < 0) {
      return {
        label: 'Overdue',
        variant: 'destructive' as const,
        color: 'text-red-600 dark:text-red-400',
        bgColor: 'bg-red-50 dark:bg-red-950/20',
        icon: AlertTriangle,
      };
    } else if (daysUntilDue <= 30) {
      return {
        label: 'Due Soon',
        variant: 'default' as const,
        color: 'text-amber-600 dark:text-amber-400',
        bgColor: 'bg-amber-50 dark:bg-amber-950/20',
        icon: Clock,
      };
    } else {
      return {
        label: 'Compliant',
        variant: 'secondary' as const,
        color: 'text-green-600 dark:text-green-400',
        bgColor: 'bg-green-50 dark:bg-green-950/20',
        icon: CheckCircle2,
      };
    }
  };

  const getDaysUntilDue = (item: ComplianceItem): number | null => {
    if (!item.nextInspectionDate) return null;
    return differenceInDays(parseISO(item.nextInspectionDate), new Date());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (complianceItems.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Regulatory Compliance
          </CardTitle>
          <CardDescription>
            Compliance requirements and inspection schedules for this asset
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12">
            <div className="flex flex-col items-center gap-3">
              <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
                <Shield className="h-8 w-8 text-muted-foreground" />
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-1">No Compliance Requirements</h3>
                <p className="text-sm text-muted-foreground">
                  This asset has no regulatory compliance requirements
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Regulatory Compliance
          </CardTitle>
          <CardDescription>
            Compliance requirements and inspection schedules for this asset
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isAssetDisposed && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-amber-900 dark:text-amber-400">
                    Asset {assetStatus}
                  </h4>
                  <p className="text-sm text-amber-700 dark:text-amber-500 mt-1">
                    This asset has been {assetStatus?.toLowerCase()}. Compliance requirements may no longer apply.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {complianceItems.map((item) => {
              const status = getComplianceStatus(item);
              const daysUntilDue = getDaysUntilDue(item);
              const StatusIcon = status.icon;

              return (
                <div
                  key={item.id}
                  className={cn(
                    'rounded-lg border p-4 transition-all hover:shadow-md',
                    status.bgColor
                  )}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-3">
                      {/* Header */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold">{item.regulation}</h4>
                            <Badge variant={status.variant} className="text-xs">
                              {StatusIcon && <StatusIcon className="mr-1 h-3 w-3" />}
                              {status.label}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {item.description || 'Regular compliance inspection required'}
                          </p>
                        </div>

                        {/* Action Menu */}
                        {!isAssetDisposed && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem onClick={() => handleActionClick(item, 'record')}>
                                <FileText className="mr-2 h-4 w-4" />
                                Record Inspection
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleActionClick(item, 'schedule')}>
                                <CalendarPlus className="mr-2 h-4 w-4" />
                                Schedule Inspection
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleActionClick(item, 'history')}>
                                <History className="mr-2 h-4 w-4" />
                                View History
                              </DropdownMenuItem>
                              <Separator className="my-1" />
                              <DropdownMenuItem onClick={() => handleActionClick(item, 'defer')}>
                                <CalendarClock className="mr-2 h-4 w-4" />
                                Defer Due Date
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleActionClick(item, 'mark-na')}>
                                <XCircle className="mr-2 h-4 w-4" />
                                Mark Non-Applicable
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>

                      {/* Details Grid */}
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <div className="text-muted-foreground">Frequency</div>
                          <div className="font-medium mt-1">{item.frequency}</div>
                        </div>

                        {item.lastInspectionDate && (
                          <div>
                            <div className="text-muted-foreground">Last Inspection</div>
                            <div className="font-medium mt-1">
                              {format(parseISO(item.lastInspectionDate), 'dd MMM yyyy')}
                            </div>
                          </div>
                        )}

                        {item.nextInspectionDate && (
                          <div>
                            <div className="text-muted-foreground">Next Inspection</div>
                            <div className={cn('font-medium mt-1', status.color)}>
                              {format(parseISO(item.nextInspectionDate), 'dd MMM yyyy')}
                            </div>
                          </div>
                        )}

                        {daysUntilDue !== null && (
                          <div>
                            <div className="text-muted-foreground">Days Until Due</div>
                            <div className={cn('font-medium mt-1', status.color)}>
                              {daysUntilDue < 0 ? (
                                <span className="flex items-center gap-1">
                                  <AlertTriangle className="h-3 w-3" />
                                  {Math.abs(daysUntilDue)} days overdue
                                </span>
                              ) : (
                                `${daysUntilDue} days`
                              )}
                            </div>
                          </div>
                        )}

                        {item.assignedInspector && (
                          <div>
                            <div className="text-muted-foreground">Assigned Inspector</div>
                            <div className="font-medium mt-1">{item.assignedInspector}</div>
                          </div>
                        )}

                        {item.certificationNumber && (
                          <div>
                            <div className="text-muted-foreground">Certification Number</div>
                            <div className="font-medium mt-1">{item.certificationNumber}</div>
                          </div>
                        )}
                      </div>

                      {/* Quick Actions */}
                      {!isAssetDisposed && (
                        <div className="flex items-center gap-2 pt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleActionClick(item, 'record')}
                          >
                            <FileText className="mr-2 h-3 w-3" />
                            Record Inspection
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleActionClick(item, 'history')}
                          >
                            <History className="mr-2 h-3 w-3" />
                            View History
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Compliance History Section */}
          <div className="pt-4">
            <Button
              variant="ghost"
              className="w-full justify-between"
              onClick={() => setShowHistory(!showHistory)}
            >
              <span className="flex items-center gap-2">
                <History className="h-4 w-4" />
                Recent Compliance History
              </span>
              {showHistory ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>

            {showHistory && (
              <div className="mt-4 space-y-3">
                {complianceItems
                  .filter((item) => item.lastInspectionDate)
                  .slice(0, 5)
                  .map((item) => (
                    <div
                      key={`history-${item.id}`}
                      className="rounded-lg border bg-card p-3 text-sm"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="font-medium">{item.regulation}</div>
                          <div className="text-muted-foreground mt-1">
                            Inspected on {format(parseISO(item.lastInspectionDate!), 'dd MMM yyyy')}
                          </div>
                          {item.lastInspectionResult && (
                            <Badge variant="secondary" className="mt-2">
                              {item.lastInspectionResult}
                            </Badge>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleActionClick(item, 'history')}
                        >
                          <History className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}

                {complianceItems.filter((item) => item.lastInspectionDate).length === 0 && (
                  <div className="text-center py-6 text-sm text-muted-foreground">
                    No inspection history available
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Action Panels */}
      {selectedItem && activeAction === 'record' && (
        <RecordInspectionPanel
          complianceItem={selectedItem as any}
          open={true}
          onOpenChange={(open) => !open && handleActionClose()}
          onSubmit={handlePanelSubmit}
        />
      )}

      {selectedItem && activeAction === 'schedule' && (
        <ScheduleInspectionPanel
          complianceItem={selectedItem as any}
          open={true}
          onOpenChange={(open) => !open && handleActionClose()}
          onSubmit={handlePanelSubmit}
        />
      )}

      {selectedItem && activeAction === 'history' && (
        <ComplianceHistoryPanel
          complianceItem={selectedItem as any}
          open={true}
          onOpenChange={(open) => !open && handleActionClose()}
        />
      )}

      {selectedItem && activeAction === 'mark-na' && (
        <MarkNonApplicablePanel
          complianceItem={selectedItem as any}
          open={true}
          onOpenChange={(open) => !open && handleActionClose()}
          onSubmit={handlePanelSubmit}
        />
      )}

      {selectedItem && activeAction === 'defer' && (
        <DeferDueDatePanel
          complianceItem={selectedItem as any}
          open={true}
          onOpenChange={(open) => !open && handleActionClose()}
          onSubmit={handlePanelSubmit}
        />
      )}
    </>
  );
}
